/**
 * User/tenant email notification preferences.
 * Backend decides whether to send; Zoho is only the delivery medium.
 * Product rule: notifications are opt-in (defaults off unless tenant/user enables).
 * UI rows and grouping come from the API preference list (type + category).
 */

export enum NotificationType {
  SL_USAGE_ABOVE_80 = 'SL_USAGE_ABOVE_80',
  SL_USAGE_ABOVE_90 = 'SL_USAGE_ABOVE_90',
  SL_STATUS_CHANGED = 'SL_STATUS_CHANGED',
  SL_CREATED = 'SL_CREATED',
  TERMINAL_ASSIGNED = 'TERMINAL_ASSIGNED',
  TERMINAL_UNASSIGNED = 'TERMINAL_UNASSIGNED',
  SL_PRODUCT_UPDATED = 'SL_PRODUCT_UPDATED',
  SL_OVERAGE_UPSERTED = 'SL_OVERAGE_UPSERTED',
  TERMINAL_STATUS_CHANGED = 'TERMINAL_STATUS_CHANGED',
  TERMINAL_IP_CHANGED = 'TERMINAL_IP_CHANGED',
}

export type NotificationCategory = 'service-line' | 'terminal';

/** Display copy for known category keys from the API. */
export const NOTIFICATION_CATEGORY_LABELS: Record<
  NotificationCategory,
  { title: string; description: string }
> = {
  'service-line': {
    title: 'Service line events',
    description: 'Alerts about data consumption thresholds and service line status changes.',
  },
  terminal: {
    title: 'Terminal events',
    description: 'Alerts when terminals change connectivity or network identity.',
  },
};

/**
 * Per-type preference from the API (effective enablement + display copy + group).
 */
export interface NotificationPreferenceItem {
  type: NotificationType | string;
  category: NotificationCategory | string;
  title: string;
  description: string;
  enabled: boolean;
}

/** PUT body: enabled map for each notification type. */
export type NotificationPreferenceOverrides = Record<string, boolean>;

/** Map of type → effective from the API list only (no catalog fill-in). */
export function preferencesToMap(
  preferences: NotificationPreferenceItem[] | undefined,
): Record<string, boolean> {
  return Object.fromEntries((preferences ?? []).map((item) => [item.type, item.enabled]));
}

export interface NotificationPreferenceGroup {
  key: NotificationCategory;
  title: string;
  description: string;
  items: NotificationPreferenceItem[];
}

/** Group API preferences by their `category` field (order preserved by first appearance). */
export function groupPreferencesByCategory(
  preferences: NotificationPreferenceItem[] | undefined,
): NotificationPreferenceGroup[] {
  const order: NotificationCategory[] = [];
  const byCategory = new Map<string, NotificationPreferenceItem[]>();

  preferences?.forEach((item) => {
    const key = item.category as NotificationCategory;
    if (!byCategory.has(key)) {
      byCategory.set(key, []);
      order.push(key);
    }
    byCategory.get(key)!.push(item);
  });

  return order.map((key) => {
    const category = NOTIFICATION_CATEGORY_LABELS[key as NotificationCategory];
    if (!category) return undefined;
    return { key, title: category.title, description: category.description, items: byCategory.get(key)! };
  }).filter((item) => item !== undefined);
}